import React, { useEffect, useRef } from 'react';

const CosmicBackground = ({ isRotating = true }) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const starsRef = useRef([]);

  useEffect(() => {
    const canvas = canvasRef?.current;
    if (!canvas) return;

    const ctx = canvas?.getContext('2d');
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Initialize stars
    const initStars = () => {
      starsRef.current = [];
      for (let i = 0; i < 200; i++) {
        starsRef?.current?.push({
          x: Math.random() * canvas?.width,
          y: Math.random() * canvas?.height,
          size: Math.random() * 2 + 0.5,
          opacity: Math.random() * 0.8 + 0.2,
          twinkleSpeed: Math.random() * 0.02 + 0.01,
          color: Math.random() > 0.7 ? '#00d4ff' : Math.random() > 0.4 ? '#ff6b35' : '#ffffff'
        });
      }
    };

    initStars();

    // Animation loop
    const animate = (timestamp) => {
      ctx?.clearRect(0, 0, canvas?.width, canvas?.height);

      // Create gradient background
      const gradient = ctx?.createRadialGradient(
        canvas?.width / 2, canvas?.height / 2, 0,
        canvas?.width / 2, canvas?.height / 2, Math.max(canvas?.width, canvas?.height) / 2
      );
      gradient?.addColorStop(0, '#1a1a2e');
      gradient?.addColorStop(0.5, '#16213e');
      gradient?.addColorStop(1, '#0f0f23');
      
      ctx.fillStyle = gradient;
      ctx?.fillRect(0, 0, canvas?.width, canvas?.height);

      // Draw and animate stars
      starsRef?.current?.forEach((star, index) => {
        // Twinkling effect
        star.opacity += Math.sin(timestamp * star?.twinkleSpeed + index) * 0.01;
        star.opacity = Math.max(0.1, Math.min(1, star?.opacity));

        // Slow drift if rotating
        if (isRotating) {
          star.x += Math.sin(timestamp * 0.0001 + index) * 0.1;
          star.y += Math.cos(timestamp * 0.0001 + index) * 0.1;

          // Wrap around screen
          if (star?.x > canvas?.width) star.x = 0;
          if (star?.x < 0) star.x = canvas?.width;
          if (star?.y > canvas?.height) star.y = 0;
          if (star?.y < 0) star.y = canvas?.height;
        }

        // Draw star
        ctx?.save();
        ctx.globalAlpha = star?.opacity;
        ctx.fillStyle = star?.color;
        ctx?.beginPath();
        ctx?.arc(star?.x, star?.y, star?.size, 0, Math.PI * 2);
        ctx?.fill();

        // Add glow effect for larger stars
        if (star?.size > 1.5) {
          ctx.shadowColor = star?.color;
          ctx.shadowBlur = star?.size * 2;
          ctx?.beginPath();
          ctx?.arc(star?.x, star?.y, star?.size * 0.5, 0, Math.PI * 2);
          ctx?.fill();
        }
        ctx?.restore();
      });

      // Draw nebula clouds
      const time = timestamp * 0.0005;
      for (let i = 0; i < 3; i++) {
        const x = canvas?.width * (0.2 + i * 0.3) + Math.sin(time + i) * 100;
        const y = canvas?.height * (0.3 + i * 0.2) + Math.cos(time + i) * 50;
        
        const nebulaGradient = ctx?.createRadialGradient(x, y, 0, x, y, 150);
        nebulaGradient?.addColorStop(0, `rgba(${i === 0 ? '0,212,255' : i === 1 ? '255,107,53' : '255,51,102'}, 0.1)`);
        nebulaGradient?.addColorStop(1, 'rgba(0,0,0,0)');
        
        ctx.fillStyle = nebulaGradient;
        ctx?.beginPath();
        ctx?.arc(x, y, 150, 0, Math.PI * 2);
        ctx?.fill();
      }

      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      if (animationRef?.current) {
        cancelAnimationFrame(animationRef?.current);
      }
    };
  }, [isRotating]);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 w-full h-full pointer-events-none z-0"
      style={{ background: 'linear-gradient(135deg, #0f0f23 0%, #1a1a2e 50%, #16213e 100%)' }}
    />
  );
};

export default CosmicBackground;